import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '../hooks/useAuth0';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import ServiceTierSelection from './ServiceTierSelection';
import TravelDetailsForm from './TravelDetailsForm';
import BookingReview from './BookingReview';
import api from '../services/api';

const steps = ['Select Service', 'Travel Details', 'Review & Pay'];

const TravelBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();

  // Get trial info from navigation state (if coming from trial card)
  const trialInfo = location.state?.trial || null;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);


  // Booking data
  const [bookingData, setBookingData] = useState({
    // Service tier
    serviceTier: '',

    // Trial info (pre-populated if coming from trial card)
    trialId: trialInfo?.nctId || '',
    trialName: trialInfo?.officialTitle || '',
    
    // Destination (from trial location or manual entry)
    destinationCity: trialInfo?.city || '',
    destinationState: trialInfo?.state || '',
    trialSiteName: trialInfo?.facility || '',
    trialSiteAddress: trialInfo?.address || '',

    // Trip dates
    tripStartDate: '',
    tripEndDate: '',

    // Contact info
    patientPhone: '',
    emergencyContact: '',
    emergencyPhone: '',

    // Special requirements
    specialRequirements: ''
  });

  const [createdBooking, setCreatedBooking] = useState(null);

    // Add this for debugging:
console.log('🔍 Trial info received:', trialInfo);
console.log('🔍 Initial booking data:', bookingData);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: '/travel/book' }
      });
    }
  }, [isAuthenticated, loginWithRedirect]);

  // Scroll to top when step changes
useEffect(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, [activeStep]);

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && !bookingData.serviceTier) {
      setError('Please select a service tier');
      return;
    }

    if (activeStep === 1) {
      // Validate travel details
      const requiredFields = [
        'destinationCity',
        'destinationState',
        'tripStartDate',
        'tripEndDate',
        'patientPhone'
      ];

      const missingFields = requiredFields.filter(field => !bookingData[field]);
      if (missingFields.length > 0) {
        setError('Please fill in all required fields');
        return;
      }
    }

    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSelectTier = (tierCode) => {
    setBookingData({ ...bookingData, serviceTier: tierCode });
    setError(null);
  };

  const handleUpdateDetails = (details) => {
    setBookingData({ ...bookingData, ...details });
  };

  const handleSubmitBooking = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/v1/travel/bookings', bookingData);
      setCreatedBooking(response.data);
      setSuccess(true);
      setActiveStep(3); // Move to success step

      // Redirect to bookings page after 3 seconds
      setTimeout(() => {
        navigate('/travel/bookings');
      }, 3000);
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.response?.data?.message || 'Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <ServiceTierSelection
            selectedTier={bookingData.serviceTier}
            onSelectTier={handleSelectTier}
          />
        );
      case 1:
        return (
          <TravelDetailsForm
            bookingData={bookingData}
            onUpdate={handleUpdateDetails}
            trialInfo={trialInfo}
          />
        );
      case 2:
        return (
          <BookingReview
            bookingData={bookingData}
            onSubmit={handleSubmitBooking}
            loading={loading}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  // Show loading while checking auth
  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Success screen
  if (success && createdBooking) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <CheckCircle sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#10b981' }}>
            Booking Confirmed!
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Your booking reference: <strong>{createdBooking.bookingReference}</strong>
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We've sent confirmation details to your email.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
            Redirecting to your bookings...
          </Typography>
          <CircularProgress size={24} sx={{ mt: 2 }} />
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f9fafb', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Book Your Travel
          </Typography>
          {trialInfo && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Booking travel for: <strong>{trialInfo.officialTitle}</strong>
            </Alert>
          )}
        </Box>

        {/* Stepper */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ mb: 4 }}>
          {getStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        {activeStep < 3 && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                size="large"
              >
                Back
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmitBooking}
                  disabled={loading}
                  size="large"
                  sx={{
                    bgcolor: '#1A5EA8',
                    px: 4,
                    '&:hover': { bgcolor: '#5568d3' }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Confirm Booking'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                  sx={{
                    bgcolor: '#1A5EA8',
                    px: 4,
                    '&:hover': { bgcolor: '#5568d3' }
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default TravelBookingPage;